import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, AlertTriangle, Info, CheckCircle2, Loader2 } from "lucide-react";

const SEV_META = {
  critical: { icon: AlertTriangle, tone: "text-rose-700 bg-rose-50 border-rose-200" },
  warning:  { icon: AlertTriangle, tone: "text-amber-800 bg-amber-50 border-amber-200" },
  info:     { icon: Info,          tone: "text-zinc-700 bg-zinc-50 border-zinc-200" },
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 50 } });
      setList(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      /* silent — notifications shouldn't break the app */
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 60000);   // poll every minute
    return () => clearInterval(id);
  }, [load]);

  const markAll = async () => {
    try {
      await api.post("/notifications/read", { ids: null });
      toast.success("All notifications marked read");
      load();
    } catch (e) { toast.error("Could not mark read"); }
  };

  return (
    <div className="relative" data-testid="notifications-bell">
      <button
        onClick={() => { setOpen(!open); if (!open) load(); }}
        className="relative p-2 rounded-lg hover:bg-zinc-100"
        data-testid="notifications-toggle"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-zinc-700" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5A00] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border border-zinc-200 rounded-xl shadow-xl z-40 overflow-hidden"
            data-testid="notifications-drawer"
          >
            <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="font-heading font-bold text-sm">Notifications</div>
              {list.length > 0 && (
                <Button variant="ghost" size="sm" onClick={markAll} data-testid="notifications-mark-all-read">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark all read
                </Button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {loading && list.length === 0 ? (
                <div className="p-6 text-center text-zinc-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
              ) : list.length === 0 ? (
                <div className="p-6 text-center text-zinc-400 text-sm">You're all caught up.</div>
              ) : list.map((n) => {
                const meta = SEV_META[n.severity] || SEV_META.info;
                const Icon = meta.icon;
                return (
                  <div
                    key={n.id}
                    className={`p-3 border-b border-zinc-100 last:border-0 ${n.read_at ? "opacity-70" : ""}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-accent px-2 py-0.5 rounded-full border ${meta.tone}`}>
                      <Icon className="w-3 h-3" /> {n.kind.replace(/_/g, " ")}
                    </div>
                    <div className="mt-1 font-semibold text-sm">{n.title}</div>
                    <div className="text-xs text-zinc-600 mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-zinc-400 mt-1 font-mono-accent">{fmt(n.created_at)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
